import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { TradePlan, StrategyRecommendation, TickerAnalysis } from '../types';
import { Clipboard, Target, ShieldAlert, Zap, PlayCircle, AlertTriangle, BookmarkPlus, Check, Loader2 } from 'lucide-react';
import { createPosition, createPositionFromAnalysis } from '../services/positionService';

interface TradePlanCardProps {
  plan: TradePlan;
  strategy: StrategyRecommendation;
  ticker: string;
  price: number; // LIVE stock price
  analysis?: TickerAnalysis; // Full analysis for tracking
}

export const TradePlanCard: React.FC<TradePlanCardProps> = ({ plan, strategy, ticker, price, analysis }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [tracking, setTracking] = useState(false);
  const [tracked, setTracked] = useState(false);
  const [trackError, setTrackError] = useState<string | null>(null);

  const getVal = (v: number | { price: number } | undefined): string => {
    if (v === undefined) return '0.00';
    if (typeof v === 'number') return v.toFixed(2);
    return v.price.toFixed(2);
  };

  const calculateSyntheticEntry = (): number => {
    if (!strategy.legs || strategy.legs.length === 0) return strategy.maxProfit / 100;
    
    return strategy.legs.reduce((total, leg) => {
      const baseMid = (leg.bid + leg.ask) / 2;
      const priceDiff = price - plan.entryPrice;
      // Formula: Mid + (Delta * Diff) + (0.5 * Gamma * Diff^2)
      const syntheticLegPrice = baseMid + (leg.delta * priceDiff) + (0.5 * (leg.gamma || 0) * Math.pow(priceDiff, 2));
      
      return leg.action === 'BUY' ? total + syntheticLegPrice : total - syntheticLegPrice;
    }, 0);
  };

  const syntheticEntry = calculateSyntheticEntry();
  
  // GAMMA RISK CALCULATION
  // If the price has moved more than 1% from the initial snapshot, Gamma risk increases
  const priceDrift = Math.abs((price - plan.entryPrice) / plan.entryPrice);
  const isHighGammaRisk = priceDrift > 0.01; 

  const copyPlan = () => {
    const text = `Trade: ${ticker} ${strategy.name}\nSynthetic Entry: $${Math.abs(syntheticEntry).toFixed(2)}\nStop: $${getVal(plan.stopLoss)}\nTarget: $${getVal(plan.takeProfit)}`;
    navigator.clipboard.writeText(text);
    alert('Live Trade plan copied!');
  };

  const handleTrackTrade = async () => {
    if (!user?.id || !analysis) {
      setTrackError('Unable to track - please sign in');
      return;
    }

    setTracking(true);
    setTrackError(null);

    try {
      const positionData = createPositionFromAnalysis(analysis, strategy, plan);
      await createPosition(user.id, positionData);
      setTracked(true);

      // Navigate to positions page after short delay
      setTimeout(() => {
        navigate('/positions');
      }, 1500);
    } catch (err) {
      setTrackError(err instanceof Error ? err.message : 'Failed to track trade');
      setTracking(false);
    }
  };

  return (
    <div className={`bg-slate-900 border ${isHighGammaRisk ? 'border-amber-500/50' : 'border-slate-800'} rounded-xl overflow-hidden mt-6 shadow-2xl transition-colors duration-500`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${isHighGammaRisk ? 'from-amber-900/40' : 'from-blue-900/50'} to-slate-900 p-3 sm:p-4 border-b border-slate-800 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2`}>
        <div className="flex items-center gap-2">
          <PlayCircle className={isHighGammaRisk ? 'text-amber-400' : 'text-blue-400'} size={20} />
          <h3 className="font-bold text-white tracking-wide uppercase text-xs">
            {isHighGammaRisk ? 'Gamma-Adjusted Execution' : 'V3 Execution Plan'}
          </h3>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {analysis && (
            <button
              onClick={handleTrackTrade}
              disabled={tracking || tracked}
              className={`text-xs font-bold flex items-center justify-center gap-1 transition-colors px-3 py-2 sm:py-1 rounded-md border flex-1 sm:flex-none ${
                tracked
                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                  : 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/30'
              } disabled:opacity-50`}
            >
              {tracking ? (
                <><Loader2 size={14} className="animate-spin" /> TRACKING...</>
              ) : tracked ? (
                <><Check size={14} /> TRACKED!</>
              ) : (
                <><BookmarkPlus size={14} /> TRACK TRADE</>
              )}
            </button>
          )}
          <button onClick={copyPlan} className="text-xs font-bold flex items-center justify-center gap-1 text-slate-300 hover:text-white transition-colors bg-slate-800 px-3 py-2 sm:py-1 rounded-md border border-slate-700 flex-1 sm:flex-none">
            <Clipboard size={14} /> COPY
          </button>
        </div>
      </div>

      <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
        {/* Entry Zone */}
        <div className="space-y-2">
          <div className="text-sm text-slate-300 font-bold uppercase flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isHighGammaRisk ? 'bg-amber-500' : 'bg-cyan-500'} animate-pulse`} /> 
            Synthetic Entry
          </div>
          <div className={`text-2xl font-mono font-bold ${isHighGammaRisk ? 'text-amber-400' : 'text-white'}`}>
            ${Math.abs(syntheticEntry).toFixed(2)}
          </div>
          <div className="text-xs leading-tight text-slate-300">
            Current Fair Value at <span className={isHighGammaRisk ? 'text-amber-400' : 'text-cyan-400'}>${price.toFixed(2)}</span>
          </div>
        </div>

        {/* Risk Management */}
        <div className="space-y-2">
          <div className="text-sm text-slate-300 font-bold uppercase flex items-center gap-2">
            <ShieldAlert size={14} className="text-rose-500" /> Stop Loss
          </div>
          <div className="text-2xl font-mono font-bold text-rose-400">
            ${getVal(plan.stopLoss)}
          </div>
          <div className="text-sm text-slate-300">
            Exit if price hits <span className="text-rose-200 font-bold">${getVal(plan.stopLoss)}</span>
          </div>
        </div>

        {/* Profit Target */}
        <div className="space-y-2">
          <div className="text-sm text-slate-300 font-bold uppercase flex items-center gap-2">
            <Target size={14} className="text-emerald-500" /> Profit Target
          </div>
          <div className="text-2xl font-mono font-bold text-emerald-400">
            ${getVal(plan.takeProfit)}
          </div>
          <div className="text-sm text-slate-300">
            Target: <span className="text-emerald-200 font-bold">${getVal(plan.takeProfit)}</span>
          </div>
        </div>
      </div>
      
      {/* Footer / Alerts */}
      <div className={`p-4 border-t border-slate-800 ${isHighGammaRisk ? 'bg-amber-950/20' : 'bg-slate-950/50'} flex flex-col gap-2`}>
        {trackError && (
          <div className="flex items-center gap-2 text-xs text-rose-400 font-bold bg-rose-400/10 p-2 rounded border border-rose-400/20 mb-1">
            <AlertTriangle size={12} />
            {trackError}
          </div>
        )}
        {tracked && (
          <div className="flex items-center gap-2 text-xs text-emerald-400 font-bold bg-emerald-400/10 p-2 rounded border border-emerald-400/20 mb-1">
            <Check size={12} />
            Trade tracked! Redirecting to positions...
          </div>
        )}
        {isHighGammaRisk && (
          <div className="flex items-center gap-2 text-xs text-amber-400 font-bold bg-amber-400/10 p-2 rounded border border-amber-400/20 mb-1">
            <AlertTriangle size={12} />
            HIGH GAMMA ALERT: Underlying has drifted { (priceDrift * 100).toFixed(1) }%. Limit order accuracy may be reduced.
          </div>
        )}
        <div className="flex items-center gap-4 text-xs">
           <div className="flex items-center gap-1 text-slate-300">
              <Zap size={10} className="text-yellow-500" />
              <span className="font-bold text-slate-300">LIVE ENGINE:</span> 
              Delta + Gamma Compiling
           </div>
        </div>
      </div>
    </div>
  );
};
